import { z } from 'zod';
import {
  listFiltersQueryFragment,
  optionalDate,
  optionalEmail,
  optionalPhone,
  optionalText,
  paginationQuerySchema,
  toPatchSchema,
  uuidSchema,
} from './common.js';
import { personNameSchema } from './auth.js';
import { businessManagerFilterSchema, type BusinessManagerRef, type RecordRef } from './records.js';

export const createContactSchema = z.object({
  firstName: optionalText(80),
  lastName: personNameSchema.max(80),
  email: optionalEmail(),
  phone: optionalPhone(),
  mobile: optionalPhone(),
  title: optionalText(120),
  department: optionalText(120),
  description: optionalText(4000),
  /** Honours an unsubscribe; the email module refuses to send when true. */
  emailOptOut: z.coerce.boolean().optional(),
  birthdate: optionalDate(),

  mailingStreet: optionalText(200),
  mailingCity: optionalText(120),
  mailingState: optionalText(120),
  mailingPostalCode: optionalText(32),
  mailingCountry: optionalText(120),

  accountId: uuidSchema.nullish(),
  reportsToId: uuidSchema.nullish(),
  /** The markets this record sells into. Carried across when a lead converts. */
  verticals: z.array(z.string().trim().min(1).max(80)).max(20).optional(),
  businessManagerId: uuidSchema.optional(),
  customFields: z.record(z.string(), z.unknown()).optional(),
});
export type CreateContactInput = z.infer<typeof createContactSchema>;

export const updateContactSchema = toPatchSchema(createContactSchema);
export type UpdateContactInput = z.infer<typeof updateContactSchema>;

export const CONTACT_SORT_FIELDS = ['createdAt', 'updatedAt', 'lastName', 'email'] as const;

export const listContactsQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(CONTACT_SORT_FIELDS).default('createdAt'),
  businessManagerId: businessManagerFilterSchema.optional(),
  accountId: uuidSchema.optional(),
  ...listFiltersQueryFragment,
});
export type ListContactsQuery = z.infer<typeof listContactsQuerySchema>;

export interface ContactSummary {
  id: string;
  firstName: string | null;
  lastName: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  title: string | null;
  account: RecordRef | null;
  verticals: string[];
  businessManager: BusinessManagerRef | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContactDetail extends ContactSummary {
  /** Where this person came from, copied off the lead at conversion. */
  leadSource: string | null;
  department: string | null;
  description: string | null;
  emailOptOut: boolean;
  birthdate: string | null;
  mailingStreet: string | null;
  mailingCity: string | null;
  mailingState: string | null;
  mailingPostalCode: string | null;
  mailingCountry: string | null;
  reportsTo: RecordRef | null;
  customFields: Record<string, unknown>;
  stats: {
    openDealCount: number;
    openDealValue: string;
  };
}
